import type { ReceiptStyleOptions } from "@/utils/receiptGenerator"

export interface LocalPrinterStyleConfig {
  renderMode: LocalPrinterRenderMode
  lineSpacing: number
  lineFeedGap: number
  titleWidth: number
  titleHeight: number
  titleBold: boolean
  bodyWidth: number
  bodyHeight: number
  bodyBold: boolean
  rasterTitleFontSize: number
  rasterBodyFontSize: number
  rasterLineHeight: number
  rasterFontFamily: LocalPrinterRasterFontFamily
}

export type LocalPrinterStylePreset = "compact" | "standard" | "large"
export type LocalPrinterTextTarget = "title" | "body"
export type LocalPrinterFontSize = "normal" | "large" | "extra-large"
export type LocalPrinterRenderMode = "native" | "raster"
export type LocalPrinterRasterFontFamily = "sans" | "serif" | "monospace"
export const DEFAULT_LOCAL_PRINTER_KEY = "RECEIPT"

export interface LocalPrinterProfileConfig {
  printWidth: string
  style: LocalPrinterStyleConfig
}

export interface LocalPrinterProfileSource {
  printWidth?: string
  localPrinterStyle?: Partial<LocalPrinterStyleConfig> | null
  localPrinterProfiles?: Record<string, {
    printWidth?: string
    style?: Partial<LocalPrinterStyleConfig> | null
  }> | null
}

const RASTER_FONT_FAMILIES: Record<LocalPrinterRasterFontFamily, string> = {
  sans: '"Microsoft YaHei UI", "Microsoft YaHei", "PingFang SC", sans-serif',
  serif: '"SimSun", "Songti SC", serif',
  monospace: '"NSimSun", "Noto Sans Mono CJK SC", monospace',
}

const TITLE_FONT_SIZE_COMMANDS: Record<LocalPrinterFontSize, { width: number, height: number }> = {
  normal: { width: 0, height: 0 },
  large: { width: 1, height: 1 },
  "extra-large": { width: 2, height: 2 },
}

const BODY_FONT_SIZE_COMMANDS: Record<LocalPrinterFontSize, { width: number, height: number }> = {
  normal: { width: 0, height: 0 },
  large: { width: 0, height: 1 },
  "extra-large": { width: 1, height: 1 },
}

export const DEFAULT_LOCAL_PRINTER_STYLE_CONFIG: LocalPrinterStyleConfig = {
  renderMode: "native",
  lineSpacing: 48,
  lineFeedGap: 0,
  titleWidth: 1,
  titleHeight: 1,
  titleBold: true,
  bodyWidth: 0,
  bodyHeight: 0,
  bodyBold: true,
  rasterTitleFontSize: 32,
  rasterBodyFontSize: 22,
  rasterLineHeight: 1.35,
  rasterFontFamily: "sans",
}

function normalizeStyleNumber(value: unknown, fallback: number, min: number, max: number) {
  const numericValue = Number(value)
  if (!Number.isFinite(numericValue)) {
    return fallback
  }
  return Math.max(min, Math.min(max, Math.round(numericValue)))
}

function normalizeDecimal(value: unknown, fallback: number, min: number, max: number) {
  const numericValue = Number(value)
  if (!Number.isFinite(numericValue)) return fallback
  return Math.max(min, Math.min(max, Math.round(numericValue * 100) / 100))
}

function normalizeRenderMode(value: unknown): LocalPrinterRenderMode {
  return value === "raster" ? "raster" : "native"
}

function normalizeRasterFontFamily(value: unknown): LocalPrinterRasterFontFamily {
  return value === "serif" || value === "monospace" ? value : "sans"
}

function normalizePrintWidth(value: unknown, fallback = "58mm") {
  const normalized = String(value || "").trim().toLowerCase()
  if (normalized.includes("80")) return "80mm"
  if (normalized.includes("58")) return "58mm"
  return fallback
}

export function resolveLocalPrinterStyleConfig(config?: Partial<LocalPrinterStyleConfig> | null): LocalPrinterStyleConfig {
  return {
    renderMode: normalizeRenderMode(config?.renderMode),
    lineSpacing: normalizeStyleNumber(config?.lineSpacing, DEFAULT_LOCAL_PRINTER_STYLE_CONFIG.lineSpacing, 24, 80),
    lineFeedGap: normalizeStyleNumber(config?.lineFeedGap, DEFAULT_LOCAL_PRINTER_STYLE_CONFIG.lineFeedGap, 0, 80),
    titleWidth: normalizeStyleNumber(config?.titleWidth, DEFAULT_LOCAL_PRINTER_STYLE_CONFIG.titleWidth, 0, 2),
    titleHeight: normalizeStyleNumber(config?.titleHeight, DEFAULT_LOCAL_PRINTER_STYLE_CONFIG.titleHeight, 0, 2),
    titleBold: config?.titleBold ?? DEFAULT_LOCAL_PRINTER_STYLE_CONFIG.titleBold,
    bodyWidth: normalizeStyleNumber(config?.bodyWidth, DEFAULT_LOCAL_PRINTER_STYLE_CONFIG.bodyWidth, 0, 1),
    bodyHeight: normalizeStyleNumber(config?.bodyHeight, DEFAULT_LOCAL_PRINTER_STYLE_CONFIG.bodyHeight, 0, 1),
    bodyBold: config?.bodyBold ?? DEFAULT_LOCAL_PRINTER_STYLE_CONFIG.bodyBold,
    rasterTitleFontSize: normalizeStyleNumber(
      config?.rasterTitleFontSize,
      DEFAULT_LOCAL_PRINTER_STYLE_CONFIG.rasterTitleFontSize,
      18,
      48,
    ),
    rasterBodyFontSize: normalizeStyleNumber(
      config?.rasterBodyFontSize,
      DEFAULT_LOCAL_PRINTER_STYLE_CONFIG.rasterBodyFontSize,
      14,
      36,
    ),
    rasterLineHeight: normalizeDecimal(
      config?.rasterLineHeight,
      DEFAULT_LOCAL_PRINTER_STYLE_CONFIG.rasterLineHeight,
      1,
      2,
    ),
    rasterFontFamily: normalizeRasterFontFamily(config?.rasterFontFamily),
  }
}

export function applyLocalPrinterStylePreset(
  config: Partial<LocalPrinterStyleConfig> | null | undefined,
  preset: LocalPrinterStylePreset,
): LocalPrinterStyleConfig {
  const normalizedConfig = resolveLocalPrinterStyleConfig(config)
  if (preset === "compact") {
    return {
      ...normalizedConfig,
      lineSpacing: 32,
      lineFeedGap: 0,
      titleWidth: 0,
      titleHeight: 0,
      titleBold: true,
      bodyWidth: 0,
      bodyHeight: 0,
      bodyBold: false,
      rasterTitleFontSize: 28,
      rasterBodyFontSize: 18,
      rasterLineHeight: 1.2,
    }
  }
  if (preset === "large") {
    return {
      ...normalizedConfig,
      lineSpacing: 64,
      lineFeedGap: 0,
      titleWidth: 2,
      titleHeight: 2,
      titleBold: true,
      bodyWidth: 0,
      bodyHeight: 1,
      bodyBold: true,
      rasterTitleFontSize: 38,
      rasterBodyFontSize: 26,
      rasterLineHeight: 1.45,
    }
  }
  return {
    ...DEFAULT_LOCAL_PRINTER_STYLE_CONFIG,
    renderMode: normalizedConfig.renderMode,
    rasterFontFamily: normalizedConfig.rasterFontFamily,
  }
}

function getFontSizeCommands(target: LocalPrinterTextTarget) {
  return target === "title" ? TITLE_FONT_SIZE_COMMANDS : BODY_FONT_SIZE_COMMANDS
}

export function applyLocalPrinterFontSize(
  config: Partial<LocalPrinterStyleConfig> | null | undefined,
  target: LocalPrinterTextTarget,
  fontSize: LocalPrinterFontSize,
): LocalPrinterStyleConfig {
  const normalizedConfig = resolveLocalPrinterStyleConfig(config)
  const size = getFontSizeCommands(target)[fontSize]
  return {
    ...normalizedConfig,
    ...(target === "title"
      ? { titleWidth: size.width, titleHeight: size.height }
      : { bodyWidth: size.width, bodyHeight: size.height }),
  }
}

export function resolveLocalPrinterFontSize(
  config: Partial<LocalPrinterStyleConfig> | null | undefined,
  target: LocalPrinterTextTarget,
): LocalPrinterFontSize | "custom" {
  const normalizedConfig = resolveLocalPrinterStyleConfig(config)
  const width = target === "title" ? normalizedConfig.titleWidth : normalizedConfig.bodyWidth
  const height = target === "title" ? normalizedConfig.titleHeight : normalizedConfig.bodyHeight
  const matched = Object.entries(getFontSizeCommands(target)).find(([, size]) => (
    size.width === width && size.height === height
  ))
  return (matched?.[0] as LocalPrinterFontSize | undefined) ?? "custom"
}

export function buildLocalPrinterReceiptStyle(
  config?: Partial<LocalPrinterStyleConfig> | null,
  printWidth?: string,
): ReceiptStyleOptions {
  const normalizedConfig = resolveLocalPrinterStyleConfig(config)
  const normalizedPrintWidth = normalizePrintWidth(printWidth)
  return {
    renderMode: normalizedConfig.renderMode,
    printWidth: normalizedPrintWidth,
    raster: {
      paperWidthDots: normalizedPrintWidth === "80mm" ? 576 : 384,
      titleFontSize: normalizedConfig.rasterTitleFontSize,
      bodyFontSize: normalizedConfig.rasterBodyFontSize,
      lineHeight: normalizedConfig.rasterLineHeight,
      fontFamily: RASTER_FONT_FAMILIES[normalizedConfig.rasterFontFamily],
      titleBold: normalizedConfig.titleBold,
      bodyBold: normalizedConfig.bodyBold,
    },
    lineSpacing: normalizedConfig.lineSpacing,
    lineFeedGap: normalizedConfig.lineFeedGap,
    titleSize: {
      width: normalizedConfig.titleWidth,
      height: normalizedConfig.titleHeight,
    },
    titleBold: normalizedConfig.titleBold,
    bodySize: {
      width: normalizedConfig.bodyWidth,
      height: normalizedConfig.bodyHeight,
    },
    bodyBold: normalizedConfig.bodyBold,
  }
}

export function resolveLocalPrinterProfile(
  source: LocalPrinterProfileSource,
  localPrinterKey = DEFAULT_LOCAL_PRINTER_KEY,
): LocalPrinterProfileConfig {
  const legacyProfile: LocalPrinterProfileConfig = {
    printWidth: normalizePrintWidth(source.printWidth),
    style: resolveLocalPrinterStyleConfig(source.localPrinterStyle),
  }
  const savedDefault = source.localPrinterProfiles?.[DEFAULT_LOCAL_PRINTER_KEY]
  const defaultProfile: LocalPrinterProfileConfig = savedDefault
    ? {
        printWidth: normalizePrintWidth(savedDefault.printWidth, legacyProfile.printWidth),
        style: resolveLocalPrinterStyleConfig(savedDefault.style ?? legacyProfile.style),
      }
    : legacyProfile
  const normalizedKey = localPrinterKey.trim() || DEFAULT_LOCAL_PRINTER_KEY
  if (normalizedKey === DEFAULT_LOCAL_PRINTER_KEY) return defaultProfile

  const savedProfile = source.localPrinterProfiles?.[normalizedKey]
  if (!savedProfile) {
    return {
      printWidth: defaultProfile.printWidth,
      style: { ...defaultProfile.style },
    }
  }
  return {
    printWidth: normalizePrintWidth(savedProfile.printWidth, defaultProfile.printWidth),
    style: resolveLocalPrinterStyleConfig(savedProfile.style ?? defaultProfile.style),
  }
}

export function buildLocalPrinterReceiptStyleForPrinter(
  source: LocalPrinterProfileSource,
  localPrinterKey = DEFAULT_LOCAL_PRINTER_KEY,
) {
  const profile = resolveLocalPrinterProfile(source, localPrinterKey)
  return buildLocalPrinterReceiptStyle(profile.style, profile.printWidth)
}
